// Translations for web apps (brain/engineering/docs/white-label-and-i18n.md §4).
// Messages are ICU MessageFormat ({count, plural, one {# task} other {# tasks}}), kept per app in
// web/src/locales/<locale>.json with stable ids, compiled ahead of time by `node ui/tools/i18n.mjs compile`,
// and formatted at run time by Lingui's core (MIT, ~2 KB gzipped; no message parser ships to the browser).
// A message missing in a language falls back to English, per message.
import { createContext, Fragment, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { localeDirection, negotiateLocale, requestedLocales, SOURCE_LOCALE, storeLocale } from './locale';
import { catalogueStatus, createTranslator, formatters, type Catalogue, type Catalogues, type Formatters, type HeldMessage, type Values } from './translator';

import { richParts, type RichTags } from './rich';
export * from './locale';
export * from './errors';
export * from './translator';
export { LanguageSelect } from './language-select';
export { richParts, safeHref, type RichTags } from './rich';

export interface I18nValue<Id extends string = string> {
  /** The message `id` in the active language, with `values` (and `{brand}`) filled in. */
  t: (id: Id, values?: Values) => string;
  /** The same message with its values held out, for reading its tags (`Rich`). */
  held: (id: Id, values?: Values) => HeldMessage;
  locale: string;
  /** Whether the active language has been reviewed by a native editor. */
  status: Catalogue['status'];
  /** Languages this app has, in the order to offer them. */
  available: readonly string[];
  /** A language's review status, known before it loads (the language menu marks drafts). */
  statusOf: (locale: string) => Catalogue['status'] | null;
  setLocale: (locale: string) => Promise<void>;
  fmt: Formatters;
}

const Ctx = createContext<I18nValue | null>(null);

/**
 * Provides translations to an app. Picks the language from ?lang=, the choice on this device, then the
 * browser; sets <html lang> and dir. Renders `fallback` until a non-English catalogue has loaded.
 */
export function I18nProvider({ catalogues, defaults, children, internalPreview = false, fallback = null }: {
  catalogues: Catalogues;
  /** Internal builds only: allow unreviewed languages. Defaults to false. */
  internalPreview?: boolean;
  /** Values every message may use besides {brand}, e.g. {app}: "Dugzi Tasks". */
  defaults?: () => Values;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const tr = useMemo(() => createTranslator(catalogues, defaults, { internalPreview }), [catalogues, defaults, internalPreview]);
  const subscribe = useCallback((cb: () => void) => tr.i18n.on('change', cb), [tr]);
  const locale = useSyncExternalStore(subscribe, () => tr.i18n.locale, () => tr.i18n.locale);
  const [ready, setReady] = useState(() => negotiateLocale(requestedLocales(), tr.available) === SOURCE_LOCALE);
  useEffect(() => {
    let live = true;
    void tr.activate(negotiateLocale(requestedLocales(), tr.available)).then(() => { if (live) setReady(true); });
    return () => { live = false; };
  }, [tr]);
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = localeDirection(locale);
  }, [locale]);
  const value = useMemo<I18nValue>(() => ({
    t: tr.t,
    held: tr.held,
    locale,
    status: tr.status(),
    available: tr.available,
    statusOf: (l: string) => catalogueStatus(catalogues, l),
    setLocale: async (l: string) => { storeLocale(l); await tr.activate(l); },
    fmt: formatters(locale),
  }), [tr, locale, catalogues]);
  // Keyed by language: a change re-renders everything, including text formatted outside React (dates).
  return <Ctx.Provider value={value}>{ready ? <Fragment key={locale}>{children}</Fragment> : fallback}</Ctx.Provider>;
}

const ENGLISH_ONLY: I18nValue = (() => {
  const tr = createTranslator({ en: { messages: {}, status: 'source' } });
  return { t: tr.t, held: tr.held, locale: SOURCE_LOCALE, status: 'source', available: [SOURCE_LOCALE], statusOf: () => 'source', setLocale: async () => {}, fmt: formatters(SOURCE_LOCALE) };
})();

/** The translator of the nearest I18nProvider (outside one: English, ids shown as is). */
export function useI18n<Id extends string = string>(): I18nValue<Id> {
  return (useContext(Ctx) ?? ENGLISH_ONLY) as I18nValue<Id>;
}

/** Shorthand for `useI18n().t`. */
export function useT<Id extends string = string>(): I18nValue<Id>['t'] {
  return useI18n<Id>().t;
}

/** A message with named tags, as in 'We sent it to <b>{to}</b>.': `tags` maps each tag to an element. */
export function Rich({ id, values, tags, allow }: { id: string; values?: Values; tags: RichTags; allow?: readonly string[] }) {
  const { text, fill } = useI18n().held(id, values);
  return <>{richParts(text, tags, fill, allow)}</>;
}

/** Marks an id for extraction where it is stored, not shown (constant tables); translate it later with t(). */
export const msg = <Id extends string>(id: Id): Id => id;
